import React, { useRef, useCallback, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import Button from 'components/Button';
import { PAGE_PATHS, ROLES } from '../../constants';
import { GridList } from 'components/GridList';
import { TagStatusFormatter } from 'components/TagStatusFormatter';
import Avatar from 'components/Avatar';
import Text from 'components/styled/Text';
import TextInput from 'components/TextInput';
import PageSearchContainer from 'components/styled/PageSearchContainer';
import PageHeader from '../../components/styled/PageHeader';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { userSelector, jobsSelector } from '../../store/selectors';
import EmployeeSelect from './components/EmployeeSelect';
import { useReduxActions } from 'hooks/useReduxActions';

const Job = () => {
  const user = useSelector(userSelector);
  const jobs = useSelector(jobsSelector);
  const { t } = useTranslation();
  const navigate = useNavigate();
  const actions = useReduxActions();
  const ref = useRef(null);

  const columnDefs = useMemo(
    () => [
      {
        headerName: t('job.header.order.id'),
        field: 'ID',
        maxWidth: 150,
        pinned: 'left',
      },
      {
        headerName: t('job.header.stage'),
        field: 'stage',
        minWidth: 150,
      },
      {
        headerName: t('job.header.status'),
        field: 'status',
        minWidth: 150,
        cellStyle: { display: 'flex', padding: '0 8px', alignItems: 'center' },
        cellRenderer: props => (
          <TagStatusFormatter status={props.data.status[user.role]} />
        ),
      },
      {
        headerName: t('job.header.team'),
        field: 'team',
        cellRenderer: props => <EmployeeSelect {...props.data} />,
        minWidth: 260,
      },
      {
        hide: user?.role === ROLES.CLIENT,
        headerName: t('job.header.client'),
        field: 'user',
        cellStyle: { display: 'flex', alignItems: 'center' },
        minWidth: 260,
        cellRenderer: props => (
          <>
            <Avatar
              size="medium"
              image={props.data.user?.profile?.avatar?.url}
            />
            <Text variant="body-small">{`${props.data.user?.profile?.first_name} ${props.data.user?.profile?.last_name}`}</Text>
          </>
        ),
      },
      {
        headerName: t('job.header.last.changes'),
        field: 'updatedAt',
        type: 'date',
        minWidth: 200,
      },
    ],
    [user], // eslint-disable-line
  );

  const redirectToCreateJob = async () => {
    try {
      const res = await actions.createNewJob();
      if (res?.ID) {
        navigate(
          `/${PAGE_PATHS.CREATE_JOB.ROOT}/${PAGE_PATHS.CREATE_JOB.INQUIRY}/${res.ID}`,
        );
      }
    } catch (err) {
      console.error(err);
    }
  };

  const handleTextInput = () => {};

  const onCellClicked = useCallback(
    event => {
      if (event.colDef.field !== 'team') {
        navigate(
          `/${PAGE_PATHS.CREATE_JOB.ROOT}/${event.data.stage}/${event.data.ID}`,
        );
      }
    },
    [], // eslint-disable-line
  );

  useEffect(() => {
    actions.getAllJobs();
    if (user.role !== ROLES.CLIENT) {
      actions.getAllEmployees();
    }
  }, []); // eslint-disable-line

  return (
    <>
      <PageHeader size="medium">
        <Text variant="title2">
          {user.role === ROLES.CLIENT
            ? t('job.title.my.orders')
            : t('job.title.orders')}
        </Text>
        <PageSearchContainer>
          {user.role === ROLES.CLIENT && (
            <Button
              size="medium"
              variant="primary"
              onClick={redirectToCreateJob}>
              {t('job.new.inquiry.button')}
            </Button>
          )}
          <TextInput
            size="medium"
            onChange={handleTextInput}
            placeholder={t('job.input.placeholder')}
          />
        </PageSearchContainer>
      </PageHeader>
      <GridList
        ref={ref}
        columnDefs={columnDefs}
        rowSelection="single"
        rows={jobs}
        onCellClicked={onCellClicked}
      />
    </>
  );
};

export default Job;
